/** ADM Audience Hub hierarchy max length. */
export const AMAZON_MAX_HIERARCHY_SEGMENT_NAME_LENGTH = 100;
/** ADM Audience Hub hierarchy min length (INT-428). */
export const AMAZON_MIN_HIERARCHY_SEGMENT_NAME_LENGTH = 5;
/** Data Provider audience name max length. */
export const AMAZON_MAX_DP_SEGMENT_NAME_LENGTH = 127;
/** Data Provider audience name min length. */
export const AMAZON_MIN_DP_SEGMENT_NAME_LENGTH = 1;

const ADM_AUDIENCE_NAME_MAX_LENGTH = 100;
const ADM_NAME_SUFFIX_LEN = 9; // `_` + 8 hex chars (see taxonomy-service amazon.rs)

// Match Rust str::trim() / char::is_whitespace() (Unicode White_Space).
const UNICODE_WHITE_SPACE_TRIM_RE = /^\p{White_Space}+|\p{White_Space}+$/gu;
const UNICODE_WHITE_SPACE_STRIP_RE = /\p{White_Space}/gu;

export const trimAmazonUnicodeWhitespace = (value: string): string =>
  value.replace(UNICODE_WHITE_SPACE_TRIM_RE, '');

export const stripAmazonUnicodeWhitespace = (value: string): string =>
  value.replace(UNICODE_WHITE_SPACE_STRIP_RE, '');

export const amazonHierarchyParts = (segmentName: string): string[] =>
  segmentName.split('>').map((part) => trimAmazonUnicodeWhitespace(part));

export const canonicalAmazonHierarchy = (segmentName: string): string =>
  amazonHierarchyParts(segmentName).join(' > ');

const slugifyAmazonHierarchyPart = (part: string): string => {
  let out = '';
  let prevSeparator = false;
  for (const c of trimAmazonUnicodeWhitespace(part)) {
    if (/\s/.test(c)) {
      if (out.length > 0 && !prevSeparator) {
        out += '-';
        prevSeparator = true;
      }
    } else if (/^[A-Za-z0-9_-]$/.test(c)) {
      out += c;
      prevSeparator = false;
    } else if (out.length > 0 && !prevSeparator) {
      out += '-';
      prevSeparator = true;
    }
  }
  while (out.endsWith('-')) {
    out = out.slice(0, -1);
  }
  return out;
};

/** Probe only; authoritative naming is in taxonomy-service/src/utils/amazon.rs */
const ADM_NAME_PROBE_SUFFIX = '_00000000';

export const amazonAdmAudienceDatasetName = (
  hierarchy: string
): string | null => {
  const parts = amazonHierarchyParts(hierarchy);
  if (parts.some((part) => part.length === 0)) {
    return null;
  }
  const slugParts = parts.map(slugifyAmazonHierarchyPart);
  if (slugParts.some((part) => part.length === 0)) {
    return null;
  }
  const baseSlug = slugParts.join('_');
  const maxBaseChars = ADM_AUDIENCE_NAME_MAX_LENGTH - ADM_NAME_SUFFIX_LEN;
  const truncatedBase = [...baseSlug].slice(0, maxBaseChars).join('');
  const name = `${truncatedBase}${ADM_NAME_PROBE_SUFFIX}`;
  if ([...name].length > ADM_AUDIENCE_NAME_MAX_LENGTH) {
    return null;
  }
  if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(name)) {
    return null;
  }
  return name;
};
